import React from 'react';
import { Input } from './@/components/ui/input';
import { Switch } from './@/components/ui/switch';
import { cn } from './@/lib/utils';

// Engine defaults from files/base_config.json — shown when a loaded config predates the section.
export const OPEN_SHORT_SHIFT_DEFAULTS = {
  MAX_HOURS: 1.5,
  PEAK_PENALTY_MULTIPLIER: 3,
  OVERAGE_PENALTY_WEIGHT: 1000,
};

const HINT_STYLE = { fontSize: 11, color: '#9ca3af', fontWeight: 400 };
const WARN_STYLE = { fontSize: 11, color: '#f59e0b', fontWeight: 500 };

const toNumberOrNull = (v) => (v === '' ? null : parseFloat(v));

/**
 * Short open shifts (`OPEN_SHORT_SHIFTS`) and their disposition (`DROP_SHORT_OPEN_SHIFTS`).
 *
 * Where no legal shift (>= the role's MIN) can tile a residual of demand — beside a hard set-shift
 * edge, or on any demand step — the LP may open an anonymous sub-MIN window instead of carrying
 * the residual as overage. Both scopes default off; every knob lives in the top-level
 * `OPEN_SHORT_SHIFTS` section, and `DROP_SHORT_OPEN_SHIFTS` decides whether a used window is shown
 * as an open TBC or removed as demand the site does not need. Mirrors the engine's CLAUDE.md.
 */
const OpenShortShiftsSection = ({ config, handleChange, kicker = '04' }) => {
  const oss = (config && config.OPEN_SHORT_SHIFTS) || {};
  const drop = (config && config.DROP_SHORT_OPEN_SHIFTS) || {};
  const constraints = (config && config.CONSTRAINTS) || {};

  const setShiftEdges = !!oss.SET_SHIFT_EDGES;
  const anywhere = !!oss.ANYWHERE;
  const anyScope = setShiftEdges || anywhere;
  const knobsDisabled = !anyScope;

  const overage = constraints.OVERAGE_PENALTY_WEIGHT ?? OPEN_SHORT_SHIFT_DEFAULTS.OVERAGE_PENALTY_WEIGHT;
  const penalty = oss.PENALTY ?? null;
  const belowParity = typeof penalty === 'number' && penalty < overage;

  const scopeRule = ({ title, sub, key, active }) => (
    <article className={cn('cfg-rule', !active && 'is-inactive')} key={key}>
      <header className="cfg-rule-head">
        <div className="cfg-rule-title-block">
          <Switch
            checked={active}
            onCheckedChange={(c) => handleChange(`OPEN_SHORT_SHIFTS.${key}`, c)}
            aria-label={title}
          />
          <h4 className="cfg-rule-title">{title}</h4>
        </div>
      </header>
      <div className="cfg-rule-body">
        <span className="cfg-field-label">{sub}</span>
      </div>
    </article>
  );

  return (
    <section className="cfg-section" data-testid="open-short-shifts-section">
      <header className="cfg-section-head">
        <span className="cfg-section-kicker">{kicker}</span>
        <h3 className="cfg-section-title">Short open shifts</h3>
        <span className="cfg-section-sub">sub-minimum open windows where no legal shift can tile demand</span>
        <div className="cfg-role-switch" style={{ marginLeft: 'auto' }}>
          <span>{anyScope ? 'open windows on' : 'open windows off'}</span>
        </div>
      </header>

      <div className="cfg-rules-grid">
        {scopeRule({
          title: 'Set-shift edges',
          sub: 'Windows ending on a hard set-shift edge. Unified LP only: set shifts are always Tier 0.',
          key: 'SET_SHIFT_EDGES',
          active: setShiftEdges,
        })}
        {scopeRule({
          title: 'Anywhere',
          sub: 'Windows ending on any demand step or set-shift edge, in both LPs. A strict superset of set-shift edges.',
          key: 'ANYWHERE',
          active: anywhere,
        })}
      </div>

      <div className="cfg-rule-fields" style={{ marginTop: 12 }}>
        <label
          className="cfg-field"
          title="Sliver cap in hours, the same on every shape. Floored to whole 30-minute slots; 0 switches the windows off."
        >
          <span className="cfg-field-label">Max window (h)</span>
          <Input
            type="number"
            min="0"
            step="0.5"
            className="cfg-num-input"
            disabled={knobsDisabled}
            placeholder={`${OPEN_SHORT_SHIFT_DEFAULTS.MAX_HOURS}`}
            value={oss.MAX_HOURS ?? ''}
            onChange={(e) => handleChange('OPEN_SHORT_SHIFTS.MAX_HOURS', toNumberOrNull(e.target.value))}
          />
        </label>
        <label
          className="cfg-field"
          title="Per body per slot. Empty follows OVERAGE_PENALTY_WEIGHT wherever the partner sets it. Hold it at parity: below it the LP trades paid overage for unfilled hours."
        >
          <span className="cfg-field-label">Penalty per body-slot</span>
          <Input
            type="number"
            min="0"
            step="any"
            className="cfg-num-input"
            disabled={knobsDisabled}
            placeholder={`= overage (${overage})`}
            value={penalty ?? ''}
            onChange={(e) => handleChange('OPEN_SHORT_SHIFTS.PENALTY', toNumberOrNull(e.target.value))}
          />
        </label>
        <label
          className="cfg-field"
          title="Multiplies the per-slot price inside a resolved peak window, so a quiet sliver is shed before a peak one. Values below 1 are clamped to 1 by the engine."
        >
          <span className="cfg-field-label">Peak multiplier</span>
          <Input
            type="number"
            min="1"
            step="0.5"
            className="cfg-num-input"
            disabled={knobsDisabled}
            placeholder={`${OPEN_SHORT_SHIFT_DEFAULTS.PEAK_PENALTY_MULTIPLIER}`}
            value={oss.PEAK_PENALTY_MULTIPLIER ?? ''}
            onChange={(e) =>
              handleChange('OPEN_SHORT_SHIFTS.PEAK_PENALTY_MULTIPLIER', toNumberOrNull(e.target.value))
            }
          />
        </label>
      </div>
      {belowParity && (
        <div style={{ ...WARN_STYLE, marginTop: 6 }} role="note">
          Penalty {penalty} is below the overage weight {overage}: the LP will shed paid overage into unfilled hours.
        </div>
      )}

      <div className="cfg-rules-grid" style={{ marginTop: 12 }}>
        <article className={cn('cfg-rule', !drop.ACTIVE && 'is-inactive')}>
          <header className="cfg-rule-head">
            <div className="cfg-rule-title-block">
              <Switch
                checked={!!drop.ACTIVE}
                onCheckedChange={(c) => handleChange('DROP_SHORT_OPEN_SHIFTS.ACTIVE', c)}
                aria-label="Remove short open shifts"
              />
              <h4 className="cfg-rule-title">Remove short open shifts</h4>
            </div>
            <div className="cfg-rule-meta">
              <label className="cfg-field" title="Rows no longer than this many hours are removed rather than shown as open TBCs.">
                <span className="cfg-field-label">up to (h)</span>
                <Input
                  type="number"
                  min="0"
                  step="0.5"
                  className="cfg-num-input"
                  disabled={!drop.ACTIVE}
                  placeholder="0"
                  value={drop.MAX_HOURS ?? ''}
                  onChange={(e) => handleChange('DROP_SHORT_OPEN_SHIFTS.MAX_HOURS', toNumberOrNull(e.target.value))}
                />
              </label>
            </div>
          </header>
          <div className="cfg-rule-body">
            <span className="cfg-field-label">
              Off: each used window is shown as an open TBC. On: a window up to the cap is removed as demand the
              site does not need, provided one body, the labour-band floor and the pooled on-site floors still hold
              at every slot; otherwise it stays a TBC with a reason. Also governs the pre-LP reconciliation open
              shifts, so one knob means one thing.
            </span>
            <span style={{ ...HINT_STYLE, display: 'block', marginTop: 6 }}>
              Telemetry: reasoning.summary.telemetry.open_short_shifts (shown) and .short_demand_drops (removed).
            </span>
          </div>
        </article>
      </div>
    </section>
  );
};

export default OpenShortShiftsSection;
